import { useEffect } from 'react';

import {
  focusActiveSiteTab,
  focusCameraSelectCard,
  getCameraSelectorSiteTabs,
  getVisibleActiveSiteCameraCards,
} from '@/components/globalCameraStreamWidget/cameraSelector/useCameraSelectorGamepadWindowEvents/useCameraSelectorGamepadWindowEvents.functions.ts';
import {
  CAMERA_SELECTOR_CONFIRM_EVENT,
  CAMERA_SELECTOR_NAVIGATE_EVENT,
} from '@/providers/gamepadProvider/gamepadProvider.constants.ts';
import { type CameraSelectorNavigateDirection } from '@/providers/gamepadProvider/gamepadProvider.help.ts';

import {
  cameraSelectCardSelector,
  cameraSelectorActiveSiteTabSelector,
  cameraSelectorSiteTabSelector,
} from './cameraSelector.constants.ts';
import { focusInitialCameraSelectCard } from './focusInitialCameraSelectCard.ts';

type UseCameraSelectorGamepadWindowEventsParams = {
  isOpen?: boolean;
  preferredCameraId?: string;
};

export const useCameraSelectorGamepadWindowEvents = ({
  isOpen = true,
  preferredCameraId,
}: UseCameraSelectorGamepadWindowEventsParams = {}) => {
  useEffect(() => {
    if (!isOpen) return;
    // Gamepad handlers dispatch custom navigate/confirm events; this effect translates those events
    // into DOM focus movement across site tabs and camera cards in the active site panel.
    const getFocusedSiteTab = () => {
      const active = document.activeElement as HTMLElement | null;
      return active?.closest<HTMLElement>(cameraSelectorSiteTabSelector) ?? null;
    };

    const getFocusedCameraCard = () => {
      const active = document.activeElement as HTMLElement | null;
      return active?.closest<HTMLButtonElement>(cameraSelectCardSelector) ?? null;
    };

    const enterActiveSiteCameraPanel = () => focusInitialCameraSelectCard(preferredCameraId);

    const findCameraCardInDirection = (
      current: HTMLButtonElement,
      cards: HTMLButtonElement[],
      direction: CameraSelectorNavigateDirection
    ) => {
      const currentRect = current.getBoundingClientRect();
      const currentCx = currentRect.left + currentRect.width / 2;
      const currentCy = currentRect.top + currentRect.height / 2;
      let best: { node: HTMLButtonElement; score: number } | null = null;

      for (const candidate of cards) {
        if (candidate === current) continue;
        const rect = candidate.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = cx - currentCx;
        const dy = cy - currentCy;
        const isInDirection =
          (direction === 'left' && dx < -2) ||
          (direction === 'right' && dx > 2) ||
          (direction === 'up' && dy < -2) ||
          (direction === 'down' && dy > 2);
        if (!isInDirection) continue;
        // Squared distance from current center — avoids a huge primary-axis weight (e.g. primary
        // * 1000 + secondary) that preferred barely-in-direction targets far off the perpendicular axis.
        const score = dx * dx + dy * dy;
        if (!best || score < best.score) {
          best = { node: candidate, score };
        }
      }

      return best?.node ?? null;
    };

    const moveFocusedSiteTab = (direction: 'up' | 'down') => {
      const tabs = getCameraSelectorSiteTabs();
      if (!tabs.length) return;

      const current = getFocusedSiteTab();
      const activeTab = document.querySelector<HTMLElement>(cameraSelectorActiveSiteTabSelector);
      const currentIndex =
        current && tabs.includes(current)
          ? tabs.indexOf(current)
          : activeTab
            ? tabs.indexOf(activeTab)
            : 0;

      const startIndex = currentIndex >= 0 ? currentIndex : 0;
      const delta = direction === 'up' ? -1 : 1;
      const nextIndex = Math.min(tabs.length - 1, Math.max(0, startIndex + delta));
      focusCameraSelectCard(tabs[nextIndex]);
    };

    const moveFocusedCameraCard = (direction: CameraSelectorNavigateDirection) => {
      const cards = getVisibleActiveSiteCameraCards();
      if (!cards.length) return;

      const current = getFocusedCameraCard();
      if (!current || !cards.includes(current)) {
        enterActiveSiteCameraPanel();
        return;
      }

      const target = findCameraCardInDirection(current, cards, direction);
      if (target) {
        focusCameraSelectCard(target);
        return;
      }

      if (direction === 'left') {
        focusActiveSiteTab();
        return;
      }

      const fallbackTarget = direction === 'up' ? cards[cards.length - 1] : cards[0];
      focusCameraSelectCard(fallbackTarget);
    };

    const handleNavigate = (direction: CameraSelectorNavigateDirection) => {
      const focusedSiteTab = getFocusedSiteTab();
      if (focusedSiteTab) {
        if (direction === 'right') {
          enterActiveSiteCameraPanel();
          return;
        }
        if (direction === 'up' || direction === 'down') {
          moveFocusedSiteTab(direction);
        }
        return;
      }

      moveFocusedCameraCard(direction);
    };

    const confirmFocusedTarget = () => {
      if (getFocusedSiteTab()) {
        enterActiveSiteCameraPanel();
        return;
      }

      const visibleCards = getVisibleActiveSiteCameraCards();
      const current = getFocusedCameraCard();
      if (!current || !visibleCards.includes(current)) {
        enterActiveSiteCameraPanel();
        return;
      }
      current.click();
    };

    const onNavigate = (ev: Event) => {
      const customEv = ev as CustomEvent<CameraSelectorNavigateDirection>;
      handleNavigate(customEv.detail);
    };
    const onConfirm = () => {
      confirmFocusedTarget();
    };
    window.addEventListener(CAMERA_SELECTOR_NAVIGATE_EVENT, onNavigate as EventListener);
    window.addEventListener(CAMERA_SELECTOR_CONFIRM_EVENT, onConfirm);
    return () => {
      window.removeEventListener(CAMERA_SELECTOR_NAVIGATE_EVENT, onNavigate as EventListener);
      window.removeEventListener(CAMERA_SELECTOR_CONFIRM_EVENT, onConfirm);
    };
  }, [isOpen, preferredCameraId]);
};
